// Aira AI API client

const LITHI_BASE = '/lithi';

export interface AiraInsight {
  prioridade: 'alta' | 'media' | 'baixa';
  titulo: string;
  descricao: string;
  acao: string;
}

export interface AiraAnalysis {
  pensamento: string;
  insights: AiraInsight[];
  resumo: string;
}

export interface AiraChatMessage {
  role: 'user' | 'assistant';
  content: string;
}

export async function analyzeContext(
  context: string,
  data: Record<string, unknown>,
): Promise<AiraAnalysis> {
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), 15000); // 15s timeout
  try {
    const res = await fetch(`${LITHI_BASE}/analyze`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ context, data }),
      signal: controller.signal,
    });
    if (!res.ok) throw new Error('Aira API error');
    return res.json();
  } finally {
    clearTimeout(timeout);
  }
}

export interface OcrReceiptResult {
  sucesso: boolean;
  estabelecimento?: string;
  cidade?: string;
  data?: string;
  valor_total?: number;
  categoria?: string;
  itens?: string[];
  observacao?: string;
  cnpj?: string;
  forma_pagamento?: string;
  erro?: string;
}

export async function ocrReceipt(
  imageBase64: string,
  mediaType: string = 'image/jpeg',
): Promise<OcrReceiptResult> {
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), 60000);
  try {
    const res = await fetch(`${LITHI_BASE}/ocr-receipt`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ imageBase64, mediaType }),
      signal: controller.signal,
    });
    if (!res.ok) throw new Error('OCR API error');
    return res.json();
  } finally {
    clearTimeout(timeout);
  }
}

export interface LeadAiAttributes {
  ai_score: number;
  ai_resumo: string;
  ai_proxima_acao: string;
  ai_temperatura: 'quente' | 'morno' | 'frio';
}

export async function generateLeadAiAttributes(
  lead: Record<string, unknown>,
): Promise<LeadAiAttributes> {
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), 20000);
  try {
    const res = await fetch(`${LITHI_BASE}/lead-ai-attributes`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ lead }),
      signal: controller.signal,
    });
    if (!res.ok) throw new Error('AI Attributes API error');
    return res.json();
  } finally {
    clearTimeout(timeout);
  }
}

export interface AiraBriefingParsed {
  segmento: string; // aves, suinos, gado, industrial, residencial
  fonte: string; // poco, rede, rio, cisterna
  problemas: string[]; // dureza, ferro, turbidez, patogenos, incrustacao, dosagem, acidez
  vazao: string; // m³/h (total do sistema)
  galpoes: number;
  cliente: string;
  projeto: string;
  extras: string[]; // equipment types to add
  // Sistemas paralelos
  sistemas_paralelos: number; // 1 = simples, 2+ = redundância
  filtros_por_sistema: number; // quantidade de filtros por linha
  capacidade_filtro: number; // m³/h por filtro individual
  bomba_cv: number; // cv da bomba (por sistema)
  volume_tanque_destino: number; // litros por tanque de destino
}

export async function parseBriefing(
  briefing: string,
): Promise<AiraBriefingParsed> {
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), 25000);
  try {
    const res = await fetch(`${LITHI_BASE}/chat`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        message: `Extraia dados estruturados deste briefing de projeto de tratamento de água. O texto pode ser transcrição de áudio (linguagem coloquial). Responda APENAS com JSON válido, sem markdown.

Briefing: "${briefing}"

Formato esperado:
{
  "segmento": "aves|suinos|gado|industrial|residencial",
  "fonte": "poco|rede|rio|cisterna",
  "problemas": ["dureza","ferro","turbidez","patogenos","incrustacao","dosagem","acidez"],
  "vazao": "número total em m³/h (some todos os sistemas se paralelo)",
  "galpoes": número_de_destinos,
  "cliente": "nome do cliente se mencionado",
  "projeto": "nome ou descrição curta do projeto",
  "extras": [],
  "sistemas_paralelos": número_de_sistemas_em_paralelo,
  "filtros_por_sistema": número_de_filtros_por_sistema,
  "capacidade_filtro": capacidade_m3h_por_filtro,
  "bomba_cv": cv_da_bomba_por_sistema,
  "volume_tanque_destino": litros_por_tanque_destino
}

Regras:
- Se não mencionado, use "" para strings, [] para arrays, 0 para números opcionais
- "dureza" = dureza, água dura, cálcio, magnésio
- "ferro" = ferro, manganês, água amarela/vermelha
- "turbidez" = turbidez, sujeira, sólidos, água barrenta, "água ruim"
- "patogenos" = bactéria, coliformes, contaminação, desinfecção
- "incrustacao" = incrustação, corrosão, entupimento nipple
- "dosagem" = cloro, dosagem, vitaminas, dosadora, saneante
- "acidez" = pH, acidez, alcalinidade
- galpoes default = 1, sistemas_paralelos default = 1
- Se mencionam "dois sistemas", "dois conjuntos", "duplicado", "um de cada lado" → sistemas_paralelos = 2
- Se mencionam "3 filtros de 18" → filtros_por_sistema = 3, capacidade_filtro = 18
- Se mencionam "bomba de 6 cv" → bomba_cv = 6
- Se mencionam "caixa d'água de 20 mil litros" → volume_tanque_destino = 20000
- Vazão total = soma de todos os sistemas. Ex: 2 sistemas × 54 m³/h = 108 m³/h → vazao = "108"
- Se mencionam "batedor", "aviário", "granja", "galpão" → segmento = industrial ou aves
- Se mencionam "Instagram", "indicação" → não afeta dados técnicos`,
        history: [],
      }),
      signal: controller.signal,
    });
    if (!res.ok) throw new Error('Briefing parse error');
    const data = await res.json();
    const text = data.response || '';
    // Extract JSON from response (may have extra text)
    const jsonMatch = text.match(/\{[\s\S]*\}/);
    if (!jsonMatch) throw new Error('No JSON in response');
    return JSON.parse(jsonMatch[0]);
  } catch (err) {
    // Return empty structure on failure
    return {
      segmento: '', fonte: '', problemas: [], vazao: '', galpoes: 1,
      cliente: '', projeto: '', extras: [],
      sistemas_paralelos: 0, filtros_por_sistema: 0, capacidade_filtro: 0,
      bomba_cv: 0, volume_tanque_destino: 0,
    };
  } finally {
    clearTimeout(timeout);
  }
}

export async function chatWithAira(
  message: string,
  crmContext?: Record<string, unknown>,
  history: AiraChatMessage[] = [],
): Promise<string> {
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), 30000); // 30s timeout
  try {
    const res = await fetch(`${LITHI_BASE}/chat`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ message, crmContext, history }),
      signal: controller.signal,
    });
    if (!res.ok) throw new Error('Aira chat error');
    const data = await res.json();
    return data.response;
  } finally {
    clearTimeout(timeout);
  }
}
